import { useQuery } from '@anchorage/graphql-react';
import type { QueryResult, ShimQueryOptions } from '@anchorage/graphql-react';
import type {
  DocumentNode,
  OperationVariables,
  TypedDocumentNode,
} from '@apollo/client';
import { NetworkStatus } from '@apollo/client';

import { usePollingLifecycle } from '../usePollingLifecycle';

// Wrapper function for useQuery. It automatically stops polling on error
// so we don't have to specify it for every query, and pauses polling while
// the tab is hidden (resume on return; refetch too, unless opted out).
// A caller that passes errorPolicy: 'all' opts out of the stop-on-error
// behavior too, since it has already opted into treating errors as
// non-fatal alongside whatever data did resolve.
//
// pollInterval semantics: omitted → 60000 (default polling); explicit 0 →
// no polling AND no visibilitychange listener, behaving like plain useQuery.
// The 0 escape hatch lets wrappers (e.g. useCardTableData) route every query
// through this hook while only paying the polling machinery when polling.
//
// Recovery after a poll error is self-healing but never automatic (no retry
// loop against a failing backend): polling resumes only on a user-driven
// vector — a later successful result (filter/pagination/manual refetch) or the
// tab regaining visibility. CEXP-21599: without this, one failed poll froze
// polling until remount.
//
// refetchOnVisible (default true): also fire an immediate `refetch()` when
// the tab regains visibility, not just `startPolling`. Set to `false` for
// queries where a background poll already keeps data fresh enough and the
// extra refetch is just a redundant round-trip.
//
// Gate a blocking loader on `fetchState`, not `loading`: `loading` covers
// refetches too, so a loader keyed on it replaces already-rendered content.
// Per-state semantics and gating: docs/apollo-v4-migration-seam.md row 12.

/** Which request, if any, is in flight; `idle` also covers skipped and errored. */
export type PollingFetchState =
  | 'idle'
  | 'initial'
  | 'refetch'
  | 'polling'
  | 'fetchingMore';

export type UsePollingQueryResult<
  TData,
  TVariables extends OperationVariables,
> = QueryResult<TData, TVariables> & {
  /** `fetchState === 'polling'`. Mutually exclusive with `loading`. */
  isPolling: boolean;
  /** `fetchState === 'initial'`; false when a poll or a page is the in-flight one. */
  initialLoading: boolean;
  fetchState: PollingFetchState;
};

const getFetchState = ({
  networkStatus,
  loading,
  hasData,
}: {
  networkStatus: NetworkStatus;
  loading: boolean;
  hasData: boolean;
}): PollingFetchState => {
  // A poll and a page carry their own status and win over the data-presence
  // split below, so neither is ever mistaken for a blocking load.
  if (networkStatus === NetworkStatus.poll) {
    return 'polling';
  }
  if (networkStatus === NetworkStatus.fetchMore) {
    return 'fetchingMore';
  }
  if (!loading) {
    return 'idle';
  }
  return hasData ? 'refetch' : 'initial';
};

export const usePollingQuery = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: ShimQueryOptions<TData, TVariables> & {
    refetchOnVisible?: boolean;
  } = {} as ShimQueryOptions<TData, TVariables> & {
    refetchOnVisible?: boolean;
  },
): UsePollingQueryResult<TData, TVariables> => {
  const {
    pollInterval = 60000,
    refetchOnVisible = true,
    ...remainingOptions
  } = options;
  const skip = remainingOptions.skip;
  const errorPolicy = remainingOptions.errorPolicy;
  const {
    refetch,
    startPolling,
    stopPolling,
    error,
    loading,
    networkStatus,
    ...remainingData
  } =
    // v4 useQuery options are a conditional type over TVariables' required-ness
    // (see @anchorage/graphql/react useQuery); the merged object can't satisfy
    // it generically, so cast at the wrapper boundary like the shim does.
    useQuery<TData, TVariables>(query, {
      pollInterval,
      ...remainingOptions,
    } as ShimQueryOptions<TData, TVariables>);

  const fetchState: PollingFetchState = getFetchState({
    networkStatus,
    loading,
    hasData: remainingData.data !== undefined,
  });

  // Both read `fetchState` so they cannot contradict it. Derived rather than
  // suppressed via notifyOnNetworkStatusChange, which would also hide a
  // genuine manual refetch's loading state.
  const isPolling = fetchState === 'polling';
  const initialLoading = fetchState === 'initial';
  // Still raw `loading`, so this stays true during pagination.
  const pollAwareLoading = loading && !isPolling;

  usePollingLifecycle({
    pollInterval,
    skip,
    error,
    stopOnError: errorPolicy !== 'all',
    refetchOnVisible,
    refetch,
    startPolling,
    stopPolling,
  });

  return {
    stopPolling,
    startPolling,
    refetch,
    error,
    loading: pollAwareLoading,
    // Exposed so consumers don't re-derive any of these from networkStatus.
    isPolling,
    initialLoading,
    fetchState,
    networkStatus,
    ...remainingData,
  };
};
